import { useEffect, useState } from "react";
import { TShirt, ShoppingCart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSeo, breadcrumbs } from "@/lib/seo";
import { PublicShell, Crumbs } from "@/components/PublicShell";
import { ShareBar } from "@/components/ShareBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LABEL = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const BuyPanel = ({ item, onDone }) => {
  const [form, setForm] = useState({ name: "", email: "", quantity: 1, note: "" });
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/fashion/public/order", {
        listing_id: item.listing_id, name: form.name.trim(), email: form.email.trim(),
        quantity: Number(form.quantity), note: form.note.trim() || null });
      toast.success(`${data.title} reserved — ${data.currency} ${data.total}`);
      onDone(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-3 border-t border-border pt-3" data-testid={`fashion-buy-panel-${item.listing_id}`}>
      <div className="grid gap-2">
        <div><Label className="text-xs">Your name</Label><Input data-testid="fashion-buy-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><Label className="text-xs">Email</Label><Input data-testid="fashion-buy-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
        <div><Label className="text-xs">How many</Label><Input data-testid="fashion-buy-qty" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} /></div>
      </div>
      <Button className="mt-3 w-full" data-testid="fashion-buy-submit" onClick={submit}        disabled={busy || form.name.trim().length < 2 || !form.email.includes("@")}>
        {busy ? "Reserving…" : "Reserve it"}
      </Button>
      <p className="mt-2 text-[11px] text-slate-500">
        The seller emails you a payment link — nothing is charged now.
      </p>
    </div>
  );
};

export default function FashionPublic() {
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState(null);
  const [ordered, setOrdered] = useState(null);
  const [castings, setCastings] = useState([]);

  useSeo({
    title: "Workwear, uniform and textile trends this season | Unity & Utility",
    description: "What is selling in workwear, uniforms, footwear, PPE and fabric this season — the buying calendar, segment movement and notes from the floor of a working industrial supplier.",
    path: "/fashion-trends",
    image: (() => {
      const pic = data?.for_sale?.find((l) => l.image_url)?.image_url
        || data?.notes?.find((n) => n.image_url)?.image_url;
      return pic ? `${BACKEND}${pic}` : undefined;
    })(),
    jsonLd: breadcrumbs([["Home", "/"], ["Fashion trends", "/fashion-trends"]]),
  });

  useEffect(() => {
    api.get("/fashion/public").then(({ data: d }) => setData(d)).catch(() => setData({ segments: [], notes: [] }));
    api.get("/casting/public").then(({ data: d }) => setCastings(d)).catch(() => setCastings([]));
  }, []);

  return (
    <PublicShell>
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-14" data-testid="fashion-public-page">
        <Crumbs items={[["Home", "/"], ["Fashion trends", null]]} />
        <p className="overline text-[#B3241B]">Textiles &amp; apparel</p>
        <h1 className="font-display mt-2 flex items-start gap-3 text-4xl font-bold leading-[1.08] tracking-tight text-[#0F172A] sm:text-5xl">
          <TShirt size={38} className="mt-1 shrink-0 text-[#002FA7]" weight="duotone" />
          What is selling this season
        </h1>
        <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600">
          Workwear, uniforms, footwear, PPE and fabric — which segments are moving, when to
          place seasonal orders, and what we are seeing from the floor of a working supplier.
        </p>
        <div className="mt-6">
          <ShareBar url={`${window.location.origin}/fashion-trends`} text="Workwear and textile trends this season from Unity & Utility" testid="fashion-public-share" compact />
        </div>

        {castings.length > 1 && (
          <div className="mt-10" data-testid="fashion-public-castings">
            <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">More casting calls open now</h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {castings.slice(1).map((c, i) => (
                <div key={c.token} data-testid={`fashion-public-casting-${i}`} className="border border-border bg-white p-4">
                  <p className="font-semibold text-[#0F172A]">{c.title}</p>
                  <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                    {[c.role_label, LABEL(c.segment || ""), c.city, c.pay_note].filter(Boolean).join(" · ")}
                  </p>
                  {c.brief && <p className="mt-2 line-clamp-3 text-sm text-slate-600">{c.brief}</p>}
                  <a href={`/casting/${c.token}`} data-testid={`fashion-public-casting-apply-${i}`}
                    className="tap-safe mt-3 inline-block border border-[#002FA7] px-3 py-2 text-xs font-semibold text-[#002FA7] hover:bg-[#EEF3FF]">
                    Apply for this
                  </a>
                </div>
              ))}
            </div>
          </div>
        )}

        {castings.length > 0 && (
          <div className="mt-6 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-4" data-testid="fashion-casting-banner">
            <p className="overline text-[#002FA7]">Casting call open</p>
            <p className="mt-1 font-display text-lg font-bold text-[#0F172A]">{castings[0].title}</p>
            <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
              {[castings[0].role_label, LABEL(castings[0].segment || ""), castings[0].city,
                castings[0].pay_note,
                `${castings[0].applications} ${castings[0].applications === 1 ? "person has" : "people have"} applied`]
                .filter(Boolean).join(" · ")}
            </p>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <a href={`/casting/${castings[0].token}`} data-testid="fashion-casting-banner-apply"
                className="tap-safe inline-block bg-[#002FA7] px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white hover:bg-[#002685]">
                Apply now
              </a>
              <ShareBar url={`${BACKEND}/api/share/casting/${castings[0].token}`}
                text={`${castings[0].role_label} casting — ${castings[0].title}`}
                testid="fashion-casting-banner-share" compact instagram />
            </div>
          </div>
        )}

        {data && (
          <>
            <div className="mt-8 border-l-2 border-[#B3241B] bg-[#FFF6E5] px-4 py-3" data-testid="fashion-public-season">
              <p className="text-sm font-semibold text-[#0F172A]">{data.season_label} season</p>
              <p className="mt-1 text-sm text-slate-600">{data.buying_note}</p>
            </div>

            {data.read && (
              <div className="mt-6 border-l-2 border-[#002FA7] bg-[#EEF3FF] px-4 py-3" data-testid="fashion-public-read">
                <p className="text-sm leading-relaxed text-[#0F172A]">{data.read.summary}</p>
                {data.read.picks?.length > 0 && (
                  <ul className="mt-2 space-y-1 text-sm text-slate-700">
                    {data.read.picks.map((p, i) => <li key={i}>· {p}</li>)}
                  </ul>
                )}
              </div>
            )}

            {data.segments?.length > 0 && (
              <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="fashion-public-segments">
                {data.segments.map((s, i) => (
                  <div key={s.segment} data-testid={`fashion-public-segment-${i}`} className="border border-border bg-white p-4">
                    <p className="font-semibold text-[#0F172A]">{LABEL(s.segment)}</p>
                    <p className={`mt-2 text-2xl font-bold ${
                      s.trend_pct == null ? "text-slate-400" : s.trend_pct > 0 ? "text-[#0B5C2E]" : "text-[#B3241B]"}`}>
                      {s.trend_pct == null ? "—" : `${s.trend_pct > 0 ? "+" : ""}${s.trend_pct}%`}
                    </p>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500">demand, last 3 months</p>
                    {s.products?.length > 0 && (
                      <p className="mt-2 text-xs text-slate-600">{s.products.join(", ")}</p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {data.for_sale?.length > 0 && (
              <div className="mt-10" data-testid="fashion-public-for-sale">
                <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">In stock now</h2>
                {ordered && (
                  <p data-testid="fashion-order-done" className="mt-3 border-l-2 border-[#0F7A3D] bg-[#EAF7EF] px-4 py-3 text-sm text-[#0B5C2E]">
                    {ordered.title} reserved — {ordered.currency} {ordered.total}. {ordered.message}
                  </p>
                )}
                <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {data.for_sale.map((l, i) => (
                    <div key={l.listing_id} data-testid={`fashion-public-sale-${i}`} className="border border-border bg-white p-4">
                      {l.image_url && (
                        <img src={`${BACKEND}${l.image_url}`} alt={l.title}
                          className="mb-3 h-40 w-full border border-border object-cover" />
                      )}
                      <p className="font-semibold text-[#0F172A]">{l.title}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">
                        {LABEL(l.segment || "")}{l.seller ? ` · ${l.seller}` : ""}
                      </p>
                      <p className="mt-2 text-lg font-bold text-[#0B5C2E]">{l.currency} {l.price}</p>
                      <p className="text-xs text-slate-500">{l.quantity} {l.unit} available</p>
                      <div className="mt-3">
                        <ShareBar url={`${BACKEND}/api/share/fashion/listing/${l.listing_id}`}
                          text={`${l.title} — ${l.currency} ${l.price}`}
                          testid={`fashion-sale-share-${i}`} compact instagram />
                      </div>
                      {buying === l.listing_id ? (
                        <BuyPanel item={l} onDone={(d) => { setOrdered(d); setBuying(null); }} />
                      ) : (
                        <Button className="mt-3 w-full" data-testid={`fashion-buy-${i}`} onClick={() => setBuying(l.listing_id)}>
                          <ShoppingCart size={16} className="mr-1.5" /> Buy
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {data.notes?.length > 0 && (
              <div className="mt-10 space-y-4" data-testid="fashion-public-notes">
                <h2 className="font-display text-base font-bold text-[#0F172A] md:text-lg">From the floor</h2>
                {data.notes.map((n, i) => (
                  <article key={i} data-testid={`fashion-public-note-${i}`} className="border border-border bg-white p-4">
                    <p className="font-semibold text-[#0F172A]">{n.title}</p>
                    <p className="mt-0.5 text-[11px] uppercase tracking-wide text-slate-500">
                      {LABEL(n.segment || "")} · {(n.created_at || "").slice(0, 10)}
                    </p>
                    <p className="mt-2 whitespace-pre-line text-sm text-slate-700">{n.body}</p>
                    {n.image_url && (
                      <img src={`${BACKEND}${n.image_url}`} alt={n.title}
                        className="mt-3 w-full max-w-sm border border-border" />
                    )}
                    {n.id && (
                      <div className="mt-3">
                        <ShareBar url={`${BACKEND}/api/share/fashion/note/${n.id}`}
                          text={n.title} testid={`fashion-note-share-${i}`} compact instagram />
                      </div>
                    )}
                  </article>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </PublicShell>
  );
}
